import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { getRolePermissions, RolePermissions } from '../config/roles';

interface UserData {
    userID: string;
    email: string;
    name: string;
    role: string;
}

interface AuthContextType {
    user: UserData | null;
    permissions: RolePermissions;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const location = useLocation();
    const [user, setUser] = useState<UserData | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [permissions, setPermissions] = useState<RolePermissions>(getRolePermissions(''));

    // Fetch user data from /me endpoint
    const fetchUserData = async (signal?: AbortSignal): Promise<boolean> => {
        try {
            const response = await authService.me(signal);

            if (response) {
                const userData: UserData = {
                    userID: response.user_id || '',
                    email: response.email || '',
                    name: response.name || '',
                    role: response.role || ''
                };

                setUser(userData);
                setPermissions(getRolePermissions(userData.role));
                setIsAuthenticated(true);
                return true;
            }
            return false;
        } catch (error) {
            if (signal?.aborted) return false;
            setUser(null);
            setPermissions(getRolePermissions(''));
            setIsAuthenticated(false);
            return false;
        } finally {
            if (!signal?.aborted) {
                setIsLoading(false);
            }
        }
    };

    // Initialize auth on mount - ONLY for CMS routes
    // Runs ONCE when component mounts, not on every route change
    useEffect(() => {
        const controller = new AbortController();

        // Check if current route is a CMS route
        const isCMSRoute = location.pathname.startsWith('/cms');

        // IMPORTANT: Skip auth initialization on OAuth callback routes
        // These routes handle their own authentication flow
        const isOAuthCallback = location.pathname.startsWith('/auth/');

        const initAuth = async () => {
            try {
                // Skip auth initialization on OAuth callback routes
                if (isOAuthCallback) {
                    setIsLoading(false);
                    return;
                }

                // Check if user just logged out - don't initialize auth
                const wasRecentlyLoggedOut = sessionStorage.getItem('recentlyLoggedOut') === 'true';
                if (wasRecentlyLoggedOut) {
                    sessionStorage.removeItem('recentlyLoggedOut');
                    // Ensure auth state is cleared
                    setUser(null);
                    setIsAuthenticated(false);
                    setPermissions(getRolePermissions(''));
                    setIsLoading(false);
                    return;
                }

                // Only initialize auth if on CMS route
                if (isCMSRoute) {
                    // Already authenticated (e.g. just logged in) — skip re-init
                    if (isAuthenticated) {
                        setIsLoading(false);
                        return;
                    }

                    setIsLoading(true);
                    const refreshResult = await authService.initializeAuth(controller.signal);
                    if (controller.signal.aborted) return;

                    if (refreshResult) {
                        await fetchUserData(controller.signal);
                    } else {
                        setIsLoading(false);
                    }
                } else {
                    // Not on CMS route - skip auth initialization
                    setIsLoading(false);
                }
            } catch (error) {
                if (controller.signal.aborted) return;
                setUser(null);
                setIsAuthenticated(false);
                setIsLoading(false);
            }
        };

        initAuth();

        return () => {
            controller.abort();
        };
    }, [isAuthenticated]); // Removed location.pathname - only depend on auth state

    // Login function (fetch user data)
    const login = async (): Promise<void> => {
        setIsLoading(true);
        await fetchUserData();
    };

    // Logout function - revoke all tokens
    const logout = async (): Promise<void> => {
        try {
            // Mark that user is logging out to prevent auth re-initialization
            sessionStorage.setItem('recentlyLoggedOut', 'true');

            // Store user email before clearing state
            const userEmail = user?.email;

            // STEP 1: Revoke tokens FIRST (while still authenticated and cookies exist)
            try {
                // Revoke access token (blacklist in Redis)
                await authService.revokeToken('access');
            } catch (error) {
                console.error('Error revoking access token (ignored):', error);
            }

            try {
                // Revoke all refresh tokens (mark as revoked in DB)
                if (userEmail) {
                    await authService.revokeAllTokens(userEmail);
                }
            } catch (error) {
                console.error('Error revoking refresh tokens (ignored):', error);
            }

            // STEP 2: Clear local state AFTER tokens are revoked
            setUser(null);
            setPermissions(getRolePermissions(''));
            setIsAuthenticated(false);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');

        } catch (error) {
            console.error('Logout error:', error);
            // Even if revocation fails, clear local state
            setUser(null);
            setPermissions(getRolePermissions(''));
            setIsAuthenticated(false);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
        } finally {
            // Clean up logout flag after logout completes
            sessionStorage.removeItem('isLoggingOut');
        }
    };

    // Refresh user data
    const refreshUser = async (): Promise<void> => {
        await fetchUserData();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                permissions,
                isAuthenticated,
                isLoading,
                login,
                logout,
                refreshUser
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};